import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBreadcrumbGate } from '@/permissions/useBreadcrumbGate';
import {
  Breadcrumb,
  type BreadcrumbItem,
  type BreadcrumbSibling,
} from '@components/Breadcrumb/Breadcrumb.tsx';

import { DESTINATIONS } from './constants';

export const useDiscoverBreadcrumb = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { gateSiblings } = useBreadcrumbGate();

  const isDiscoverRoute = pathname === '/discover' || pathname.startsWith('/discover/');
  const current = DESTINATIONS.find(
    (destination) => pathname === destination.to || pathname.startsWith(`${destination.to}/`),
  );

  const items = current
    ? [{ label: 'Discover', onClick: () => navigate('/discover') }, { label: current.label }]
    : [{ label: 'Discover' }];

  // Same question the cards on the index ask, and the same answers: a catalogue the owner's
  // plan withholds keeps its row with a padlock and lands on the placeholder, one nobody
  // could reach is not offered. The hub itself needs no rights.
  const siblings: BreadcrumbSibling[] = gateSiblings([
    { label: 'Discover', current: !current, onClick: () => navigate('/discover') },
    ...DESTINATIONS.map((destination) => ({
      label: destination.label,
      current: destination === current,
      onClick: () => navigate(destination.to),
      anyOf: destination.anyOf,
    })),
  ]);

  return { isDiscoverRoute, items, siblings };
};

export const useDiscoverBreadcrumbTitle = (): ReactNode | null => {
  const { isDiscoverRoute, items, siblings } = useDiscoverBreadcrumb();

  if (!isDiscoverRoute) return null;

  return <Breadcrumb items={items} siblings={siblings} />;
};

export type DiscoverRecord = { id: string; label: string };

/**
 * The header for a record that hangs off one of Discover's lists — a provider,
 * a CV: Discover › <list> › <record>, with the record's peers in the leaf's
 * switcher. The way up lives in the header itself, which is why these pages
 * carry no back button.
 *
 * `peers` is the list's own first page, so the record being viewed can be
 * missing from it (a later page, or a record the catalogue doesn't list at all).
 * It is added to the top of the switcher rather than left unmarked.
 */
export const useDiscoverRecordBreadcrumb = ({
  listRoute,
  recordPath,
  record,
  peers,
}: {
  listRoute: string;
  recordPath: (id: string) => string;
  record: DiscoverRecord;
  peers: DiscoverRecord[];
}) => {
  const navigate = useNavigate();
  const { gateCrumb, gateParent } = useBreadcrumbGate();

  const list = DESTINATIONS.find(({ to }) => to === listRoute);
  const listLabel = list?.label ?? 'Discover';
  const openList = () => navigate(listRoute);
  // A record's own route carries no gate — it is reachable from a chat, a task or a link —
  // so the way back up to the catalogue is the rung that has to ask.
  const listCrumb = { label: listLabel, onClick: openList };

  // Same LIMIT/OFFSET lists the catalogue pages read, so the same record can
  // come back twice — the switcher would otherwise offer it twice.
  const rows: DiscoverRecord[] = [];
  const seen = new Set<string>();
  for (const peer of peers) {
    if (seen.has(peer.id)) continue;
    seen.add(peer.id);
    rows.push(peer);
  }
  if (!seen.has(record.id)) rows.unshift(record);

  return {
    items: [
      { label: 'Discover', onClick: () => navigate('/discover') },
      gateCrumb(listCrumb, list?.anyOf),
      { label: record.label },
    ] satisfies BreadcrumbItem[],
    siblings: rows.map(({ id, label }) => ({
      label,
      current: id === record.id,
      onClick: () => navigate(recordPath(id)),
    })) satisfies BreadcrumbSibling[],
    parent: gateParent(listCrumb, list?.anyOf),
  };
};
